import {
  EMPTY_SLIDE_LAYOUT,
  slideTextFontCssFamily,
  type ProgramSlideImageBlockV1,
  type ProgramSlideLayoutV1,
  type ProgramSlidePolylineBlockV1,
  type ProgramSlideTableBlockV1,
  type ProgramSlideTextBoxV1,
} from '../types/programContentSlideCanvas';
import { normalizeWeeklyGuideColorHex } from '../constants/weeklyLearningGuide';
import { lightenRgbHexTowardWhite } from './hexColorLighten';
import { getTableAnchorCell, isTableCellCovered } from './programSlideTableBlock';

export type ProgramSlideOfflineHtmlTitle = {
  learningTitle: string;
  achievementText: string;
  learningMemoNote: string;
  evaluationMemoNote: string;
  track: string;
  unit: string;
  pageMain: string;
  pageAssist: string;
  program: string;
  weeklyTrackColorHex?: string;
};

export type ProgramSlideOfflineHtmlExportParams = {
  includeTitleSlide: boolean;
  title: ProgramSlideOfflineHtmlTitle;
  layouts: Record<number, ProgramSlideLayoutV1>;
  extraPageCount: number;
  editableSlideOrder: number[];
  fileNameHint?: string;
  /** 편집 캔버스 가로/세로. 없으면 라이브 캔버스·원 도형에서 추정 */
  canvasAspect?: number;
};

const DEFAULT_SLIDE_ASPECT = 16 / 9;
const DEFAULT_CANVAS_BG = '#ffffff';

function resolveLayoutPageBackground(layout: ProgramSlideLayoutV1 | undefined): string {
  const hex = (layout?.pageBackgroundColorHex || layout?.defaultBackgroundColorHex || '').trim();
  return /^#[0-9a-fA-F]{3,8}$/.test(hex) ? hex : DEFAULT_CANVAS_BG;
}

function clampSlideAspect(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return DEFAULT_SLIDE_ASPECT;
  return Math.min(3.2, Math.max(1.05, raw));
}

export function readLiveSlideCanvasAspect(): number | null {
  const el = document.querySelector('.cp-program-slide-canvas-inner');
  if (!(el instanceof HTMLElement)) return null;
  const rect = el.getBoundingClientRect();
  if (rect.width < 40 || rect.height < 40) return null;
  return clampSlideAspect(rect.width / rect.height);
}

function inferCanvasAspectFromLayouts(layouts: Record<number, ProgramSlideLayoutV1>): number | null {
  const ratios: number[] = [];
  for (const layout of Object.values(layouts)) {
    for (const shape of layout.shapes ?? []) {
      if (shape.shape !== 'circle') continue;
      if (shape.w < 0.01 || shape.h < 0.01) continue;
      /* 원은 픽셀 정사각: w * canvasW = h * canvasH → canvasW/H = h/w */
      ratios.push(shape.h / shape.w);
    }
  }
  if (!ratios.length) return null;
  ratios.sort((a, b) => a - b);
  return clampSlideAspect(ratios[Math.floor(ratios.length / 2)] ?? DEFAULT_SLIDE_ASPECT);
}

export function resolveOfflineSlideCanvasAspect(
  layouts: Record<number, ProgramSlideLayoutV1>,
  preferred?: number
): number {
  if (preferred && Number.isFinite(preferred) && preferred > 0) return clampSlideAspect(preferred);
  return readLiveSlideCanvasAspect() ?? inferCanvasAspectFromLayouts(layouts) ?? DEFAULT_SLIDE_ASPECT;
}

function escapeHtml(raw: string): string {
  return raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeAttr(raw: string): string {
  return escapeHtml(raw).replace(/'/g, '&#39;');
}

function linesFromText(raw: string): string[] {
  return (raw ?? '').replace(/\r\n/g, '\n').split('\n');
}

function sanitizeDownloadStem(raw: string): string {
  const trimmed = raw.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
  return trimmed.slice(0, 48) || '수업프레젠테이션';
}

function defaultOfflineHtmlFilename(hint: string): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${sanitizeDownloadStem(hint)}-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}.html`;
}

function downloadTextFile(contents: string, filename: string) {
  const blob = new Blob([contents], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function isInlineableImageSrc(src: string): boolean {
  const trimmed = src.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('data:image/')) return true;
  if (trimmed.startsWith('blob:')) return true;
  return /^https?:\/\//i.test(trimmed);
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('이미지를 읽지 못했습니다.'));
    reader.readAsDataURL(blob);
  });
}

async function fetchImageAsDataUrl(src: string): Promise<string> {
  const trimmed = src.trim();
  if (trimmed.startsWith('data:image/')) return trimmed;
  const res = await fetch(trimmed);
  if (!res.ok) throw new Error(`이미지 응답 ${res.status}`);
  const blob = await res.blob();
  if (!blob.type.startsWith('image/') && blob.size <= 0) {
    throw new Error('이미지 데이터가 비어 있습니다.');
  }
  return await blobToDataUrl(blob);
}

async function inlineLayoutImages(
  layout: ProgramSlideLayoutV1,
  cache: Map<string, string>
): Promise<ProgramSlideLayoutV1> {
  const next = structuredClone(layout) as ProgramSlideLayoutV1;
  const rewrite = async (image: ProgramSlideImageBlockV1): Promise<ProgramSlideImageBlockV1> => {
    const src = (image.src ?? '').trim();
    if (!isInlineableImageSrc(src)) return { ...image, src: '' };
    const cached = cache.get(src);
    if (cached != null) return { ...image, src: cached };
    try {
      const dataUrl = await fetchImageAsDataUrl(src);
      cache.set(src, dataUrl);
      return { ...image, src: dataUrl };
    } catch {
      cache.set(src, '');
      return { ...image, src: '' };
    }
  };
  next.images = await Promise.all((next.images ?? []).map(rewrite));
  next.clipboardImages = await Promise.all((next.clipboardImages ?? []).map(rewrite));
  next.scanImages = await Promise.all((next.scanImages ?? []).map(rewrite));
  return next;
}

function fontCss(fontFamily: ProgramSlideTextBoxV1['fontFamily']): string {
  return slideTextFontCssFamily(fontFamily);
}

function textBoxInnerHtml(box: ProgramSlideTextBoxV1): string {
  if (box.textRuns && box.textRuns.length > 0) {
    return box.textRuns
      .map((run) => {
        const color = (run.colorHex ?? box.colorHex ?? '').trim();
        const style = color ? ` style="color:${escapeAttr(color)}"` : '';
        return `<span${style}>${escapeHtml(run.text).replace(/\n/g, '<br>')}</span>`;
      })
      .join('');
  }
  return escapeHtml(box.text ?? '').replace(/\n/g, '<br>');
}

function imageCropStyle(image: ProgramSlideImageBlockV1): string {
  const crop = image.crop;
  const w = Math.max(0.001, crop?.w ?? 1);
  const h = Math.max(0.001, crop?.h ?? 1);
  const x = crop?.x ?? 0;
  const y = crop?.y ?? 0;
  return [
    `width:${100 / w}%`,
    `height:${100 / h}%`,
    `left:${(-x / w) * 100}%`,
    `top:${(-y / h) * 100}%`,
  ].join(';');
}

function renderImageBlock(image: ProgramSlideImageBlockV1, key: string): string {
  const style = [
    `left:${image.x * 100}%`,
    `top:${image.y * 100}%`,
    `width:${image.w * 100}%`,
    `height:${image.h * 100}%`,
    `z-index:${image.zIndex ?? 0}`,
  ].join(';');
  if (!image.src) {
    return `<div class="blk img" style="${style}"><div class="ph">이미지를 불러오지 못했습니다</div></div>`;
  }
  return `<div class="blk img" style="${style}"><img src="${escapeAttr(image.src)}" alt="" style="${imageCropStyle(image)}"></div>`;
}

function renderTable(table: ProgramSlideTableBlockV1): string {
  const rows: string[] = [];
  const rowHeights = table.rowHeightsFrac ?? [];
  const colWidths = table.colWidthsFrac ?? [];
  const border = `${Math.max(0, table.borderWidthPx ?? 1)}px solid ${escapeAttr(table.borderColorHex || '#333')}`;
  for (let row = 0; row < table.rowCount; row += 1) {
    const cells: string[] = [];
    for (let col = 0; col < table.colCount; col += 1) {
      if (isTableCellCovered(table, row, col)) continue;
      const anchor = getTableAnchorCell(table, row, col);
      if (!anchor) continue;
      const { cell, anchorCol } = anchor;
      const colWidthPct = colWidths.slice(anchorCol, anchorCol + cell.colSpan).reduce((a, b) => a + b, 0) * 100;
      const vAlign = cell.verticalAlign === 'top' ? 'top' : cell.verticalAlign === 'bottom' ? 'bottom' : 'middle';
      cells.push(
        `<td rowspan="${cell.rowSpan}" colspan="${cell.colSpan}" style="width:${colWidthPct}%;text-align:${escapeAttr(
          cell.textAlign
        )};vertical-align:${vAlign};color:${escapeAttr(cell.colorHex)};background:${escapeAttr(
          cell.backgroundColorHex
        )};font-weight:${cell.bold ? 700 : 400};border:${border}">${escapeHtml(cell.text || '\u00a0').replace(
          /\n/g,
          '<br>'
        )}</td>`
      );
    }
    const rowH = rowHeights[row];
    const trStyle = Number.isFinite(rowH) ? ` style="height:${(rowH as number) * 100}%"` : '';
    rows.push(`<tr${trStyle}>${cells.join('')}</tr>`);
  }
  const wrap = [
    `left:${table.x * 100}%`,
    `top:${table.y * 100}%`,
    `width:${table.w * 100}%`,
    `height:${table.h * 100}%`,
    `z-index:${table.zIndex ?? 0}`,
    `font-size:${table.defaultFontSizePx ?? 14}px`,
    `font-family:${escapeAttr(fontCss(table.defaultFontFamily))}`,
  ].join(';');
  return `<div class="blk table" style="${wrap}"><table>${rows.join('')}</table></div>`;
}

function polylinePointsAttr(pl: ProgramSlidePolylineBlockV1): string {
  return pl.points.map((p) => `${p.x},${p.y}`).join(' ');
}

function renderLinkCard(opts: {
  x: number;
  y: number;
  w: number;
  h: number;
  zIndex: number;
  kindLabel: string;
  title: string;
  href: string;
}): string {
  const style = [
    `left:${opts.x * 100}%`,
    `top:${opts.y * 100}%`,
    `width:${opts.w * 100}%`,
    `height:${opts.h * 100}%`,
    `z-index:${opts.zIndex}`,
  ].join(';');
  const href = opts.href.trim();
  const label = escapeHtml(opts.title || href || opts.kindLabel);
  const inner = href
    ? `<a href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer">${label}</a><span class="hint">${escapeHtml(
        opts.kindLabel
      )} · 인터넷이 필요합니다</span>`
    : `<span>${escapeHtml(opts.kindLabel)}</span><span class="hint">연결된 주소가 없습니다</span>`;
  return `<div class="blk link-card" style="${style}">${inner}</div>`;
}

function renderCanvasSlide(layout: ProgramSlideLayoutV1, canvasAspect: number, navLabel: string): string {
  const bg = resolveLayoutPageBackground(layout);
  const textColor = (layout.pageTextColorHex || layout.defaultTextColorHex || '#1a1a1a').trim();
  const parts: string[] = [];

  for (const box of layout.boxes ?? []) {
    const style = [
      `left:${box.x * 100}%`,
      `top:${box.y * 100}%`,
      `width:${box.w * 100}%`,
      `height:${box.h * 100}%`,
      `z-index:${box.zIndex ?? 0}`,
      `font-size:${box.fontSizePx}px`,
      `font-family:${escapeAttr(fontCss(box.fontFamily))}`,
      `color:${escapeAttr(box.colorHex)}`,
      `font-weight:${box.bold ? 700 : 400}`,
      `text-align:${escapeAttr(box.textAlign ?? 'left')}`,
      `line-height:${box.lineHeight ?? 1.05}`,
    ].join(';');
    parts.push(`<div class="blk text" style="${style}">${textBoxInnerHtml(box)}</div>`);
  }

  for (const image of [...(layout.images ?? []), ...(layout.clipboardImages ?? []), ...(layout.scanImages ?? [])]) {
    parts.push(renderImageBlock(image, image.id));
  }

  for (const shape of layout.shapes ?? []) {
    const isCircle = shape.shape === 'circle';
    const circleH = isCircle ? shape.w * canvasAspect : shape.h;
    const circleTop = isCircle ? shape.y + (shape.h - circleH) / 2 : shape.y;
    const style = [
      `left:${shape.x * 100}%`,
      `top:${circleTop * 100}%`,
      `width:${shape.w * 100}%`,
      isCircle ? 'height:auto;aspect-ratio:1' : `height:${shape.h * 100}%`,
      `z-index:${shape.zIndex ?? 0}`,
      `background:${escapeAttr(shape.fillColorHex || 'transparent')}`,
      `border:${Math.max(0, shape.strokeWidthPx)}px solid ${escapeAttr(shape.strokeColorHex || 'transparent')}`,
      `border-radius:${isCircle ? '50%' : `${shape.cornerRadiusPx ?? 0}px`}`,
      `color:${escapeAttr(shape.colorHex)}`,
      `font-size:${shape.fontSizePx}px`,
      `font-family:${escapeAttr(fontCss(shape.fontFamily))}`,
      `font-weight:${shape.bold ? 700 : 400}`,
      `text-align:${escapeAttr(shape.textAlign ?? 'center')}`,
    ].join(';');
    parts.push(
      `<div class="blk shape${isCircle ? ' is-circle' : ''}" style="${style}">${escapeHtml(shape.text).replace(
        /\n/g,
        '<br>'
      )}</div>`
    );
  }

  for (const pl of layout.polylines ?? []) {
    if (!pl.points.length) continue;
    parts.push(
      `<svg class="blk line" viewBox="0 0 1 1" preserveAspectRatio="none" style="z-index:${pl.zIndex ?? 0}">` +
        `<polyline points="${escapeAttr(polylinePointsAttr(pl))}" fill="none" stroke="${escapeAttr(
          pl.strokeColorHex || '#111'
        )}" stroke-width="${Math.max(1, pl.strokeWidthPx)}" vector-effect="non-scaling-stroke" stroke-linecap="round" stroke-linejoin="round"/>` +
        `</svg>`
    );
  }

  for (const table of layout.tables ?? []) {
    parts.push(renderTable(table));
  }

  for (const button of layout.buttons ?? []) {
    const style = [
      `left:${button.x * 100}%`,
      `top:${button.y * 100}%`,
      `width:${button.w * 100}%`,
      `height:${button.h * 100}%`,
      `z-index:${button.zIndex ?? 0}`,
      `background:${escapeAttr(button.backgroundColorHex)}`,
      `color:${escapeAttr(button.colorHex)}`,
      `font-size:${button.fontSizePx}px`,
      `font-family:${escapeAttr(fontCss(button.fontFamily))}`,
      `font-weight:${button.bold ? 700 : 400}`,
      `border-radius:${button.cornerRadiusPx}px`,
    ].join(';');
    if (button.kind === 'link' && button.href.trim()) {
      parts.push(
        `<a class="blk btn" style="${style}" href="${escapeAttr(button.href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(
          button.text
        )}</a>`
      );
    } else {
      parts.push(`<div class="blk btn" style="${style}">${escapeHtml(button.text)}</div>`);
    }
  }

  for (const video of layout.videos ?? []) {
    parts.push(
      renderLinkCard({
        x: video.x,
        y: video.y,
        w: video.w,
        h: video.h,
        zIndex: video.zIndex ?? 0,
        kindLabel: '외부 영상',
        title: '영상 링크 열기',
        href: video.src,
      })
    );
  }

  for (const wp of layout.webpages ?? []) {
    const href = (wp.mode === 'code' ? '' : wp.url) || wp.url;
    parts.push(
      renderLinkCard({
        x: wp.x,
        y: wp.y,
        w: wp.w,
        h: wp.h,
        zIndex: wp.zIndex ?? 0,
        kindLabel: wp.mode === 'code' ? '코드 블럭' : '웹페이지',
        title: (wp.title || href || '웹페이지 링크').trim(),
        href,
      })
    );
  }

  for (const quiz of layout.quizzes ?? []) {
    const style = [
      `left:${quiz.x * 100}%`,
      `top:${quiz.y * 100}%`,
      `width:${quiz.w * 100}%`,
      `height:${quiz.h * 100}%`,
      `z-index:${quiz.zIndex ?? 0}`,
      `background:${escapeAttr(quiz.backgroundColorHex || 'rgba(255,255,255,0.95)')}`,
      `color:${escapeAttr(quiz.colorHex)}`,
      `font-size:${quiz.fontSizePx}px`,
      `font-family:${escapeAttr(fontCss(quiz.fontFamily))}`,
      `font-weight:${quiz.bold ? 700 : 400}`,
      `border-radius:${quiz.cornerRadiusPx}px`,
    ].join(';');
    parts.push(
      `<div class="blk quiz-short" data-quiz-id="${escapeAttr(quiz.id)}" data-answer="${escapeAttr(
        quiz.correctAnswer ?? ''
      )}" style="${style}">` +
        `<input class="quiz-short-input" type="text" autocomplete="off" spellcheck="false" placeholder="${escapeAttr(
          quiz.placeholder || '정답을 입력하세요'
        )}">` +
        `</div>`
    );
  }

  const choiceCorrectBySet: Record<string, number[]> = {};
  for (const set of layout.quizChoiceSets ?? []) {
    choiceCorrectBySet[set.id] = set.correctOptionIndices ?? [];
  }
  for (const option of layout.quizChoiceOptions ?? []) {
    const correct = choiceCorrectBySet[option.setId] ?? [];
    const style = [
      `left:${option.x * 100}%`,
      `top:${option.y * 100}%`,
      `width:${option.w * 100}%`,
      `height:${option.h * 100}%`,
      `z-index:${option.zIndex ?? 0}`,
      `background:${escapeAttr(option.backgroundColorHex || 'rgba(255,255,255,0.95)')}`,
      `color:${escapeAttr(option.colorHex)}`,
      `font-size:${option.fontSizePx}px`,
      `font-family:${escapeAttr(fontCss(option.fontFamily))}`,
      `font-weight:${option.bold ? 700 : 400}`,
      `border-radius:${option.cornerRadiusPx}px`,
    ].join(';');
    parts.push(
      `<button type="button" class="blk quiz-choice" data-set-id="${escapeAttr(option.setId)}" data-index="${
        option.optionIndex
      }" data-correct="${escapeAttr(correct.join(','))}" style="${style}">` +
        `<span>${escapeHtml(option.label)}</span>` +
        `</button>`
    );
  }

  return `<section class="slide canvas-slide" data-nav-label="${escapeAttr(navLabel)}" data-bg="${escapeAttr(
    bg
  )}" style="--slide-bg:${escapeAttr(bg)};--slide-fg:${escapeAttr(textColor)}">${parts.join('')}</section>`;
}

function renderTitleSlide(title: ProgramSlideOfflineHtmlTitle, slideBg: string): string {
  const learning = (title.learningTitle || '').trim();
  const criteria = linesFromText(title.achievementText).filter((l) => l.trim());
  const memo = linesFromText(title.learningMemoNote);
  const evaluation = linesFromText(title.evaluationMemoNote);
  const tab = (id: string, label: string, active = false) =>
    `<button type="button" class="title-tab${active ? ' is-on' : ''}" data-tab="${id}">${escapeHtml(label)}</button>`;
  const panel = (id: string, body: string, active = false) =>
    `<div class="title-panel${active ? ' is-on' : ''}" data-panel="${id}">${body}</div>`;
  const listHtml = (lines: string[], empty: string) =>
    lines.some((l) => l.trim())
      ? `<div class="title-lines">${lines.map((l) => `<div>${escapeHtml(l || '\u00a0')}</div>`).join('')}</div>`
      : `<div class="title-empty">${escapeHtml(empty)}</div>`;
  return `<section class="slide title-slide" data-nav-label="타이틀" style="--slide-bg:${escapeAttr(slideBg)}">
    <div class="title-tabs">${tab('title', '타이틀', true)}${tab('criteria', '성취기준')}${tab(
      'learningMemo',
      '학습메모'
    )}${tab('evaluation', '평가계획')}</div>
    ${panel('title', learning ? `<div class="title-hero">${escapeHtml(learning)}</div>` : `<div class="title-empty">등록된 학습내용이 없습니다.</div>`, true)}
    ${panel('criteria', listHtml(criteria, '등록된 성취기준이 없습니다.'))}
    ${panel('learningMemo', listHtml(memo, '등록된 학습메모가 없습니다.'))}
    ${panel('evaluation', listHtml(evaluation, '등록된 평가 메모가 없습니다.'))}
  </section>`;
}

function renderOfflineHero(title: ProgramSlideOfflineHtmlTitle): string {
  const track = title.track.trim();
  const unit = title.unit.trim();
  const program = title.program.trim() || title.learningTitle.trim();
  const pageMain = title.pageMain.trim();
  const pageAssist = title.pageAssist.trim();
  const weeklyHex = normalizeWeeklyGuideColorHex(title.weeklyTrackColorHex ?? '');
  const trackStyle = weeklyHex
    ? ` style="background:${escapeAttr(lightenRgbHexTowardWhite(weeklyHex, 0.8))};color:#1f2937;border-color:rgba(0,0,0,0.22)"`
    : '';
  const pages = [pageMain, pageAssist].filter(Boolean).join(' ');
  return `<section class="hero" aria-label="수업 정보">
    <div class="hero-meta">
      ${track ? `<span class="hero-track"${trackStyle}>${escapeHtml(track)}</span>` : ''}
      ${unit ? `<div class="hero-unit">${escapeHtml(unit)}</div>` : ''}
      ${pages ? `<div class="hero-pages">${escapeHtml(pages)}</div>` : ''}
    </div>
    <div class="hero-program">${escapeHtml(program || '학습내용')}</div>
  </section>`;
}

function buildOfflineHtmlDocument(params: {
  heading: string;
  title: ProgramSlideOfflineHtmlTitle;
  slidesHtml: string;
  canvasAspect: number;
  canvasBg: string;
}): string {
  return `<!DOCTYPE html>
<html lang="ko">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(params.heading)}</title>
<style>
:root { color-scheme: light; }
* { box-sizing: border-box; }
html, body { margin: 0; height: 100%; background: #f4f4f5; color: #1a1a1a; font-family: -apple-system, BlinkMacSystemFont, 'Apple SD Gothic Neo', 'Malgun Gothic', sans-serif; }
body { display: flex; flex-direction: column; }
.bar { display: flex; align-items: center; justify-content: center; padding: 10px 16px; background: #1c1c1e; color: #fff; }
.bar h1 { margin: 0; font-size: 16px; font-weight: 800; letter-spacing: -0.02em; text-align: center; }
.hero { flex: 0 0 auto; background: #f4f4f5; color: #1a1a1a; padding: 10px 20px 8px; text-align: center; border-bottom: 1px solid rgba(128, 132, 138, 0.32); }
.hero-meta { display: flex; flex-wrap: wrap; align-items: center; justify-content: center; column-gap: 10px; row-gap: 2px; }
.hero-track { display: inline-block; padding: 4px 12px; font-size: 14px; font-weight: 600; border-radius: 10px; border: 1px solid #a8aeb6; background: #f6e58d; color: #1f2937; }
.hero-unit { font-weight: 800; font-size: clamp(16px, 2.2vw, 28px); letter-spacing: -0.03em; }
.hero-pages { font-weight: 600; font-size: 16px; color: #6b7280; }
.hero-program { margin-top: 2px; font-weight: 600; font-size: clamp(20px, 2.6vw, 34px); letter-spacing: -0.02em; }
.stage { flex: 1; min-height: 0; display: flex; align-items: stretch; justify-content: stretch; padding: 0; }
.frame { --slide-aspect: ${params.canvasAspect}; width: 100%; height: 100%; background: ${params.canvasBg}; position: relative; overflow: hidden; }
.slide { position: absolute; inset: 0; display: none; background: var(--slide-bg, ${params.canvasBg}); color: var(--slide-fg, #1a1a1a); }
.slide.is-on { display: block; }
.nav { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; column-gap: 12px; padding: 6px 16px 8px; background: rgba(128, 132, 138, 0.16); border-top: 1px solid rgba(128, 132, 138, 0.32); }
.nav-btn { flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; min-width: 34px; height: 30px; padding: 0 8px; border-radius: 6px; border: 1px solid rgba(123, 129, 138, 0.9); background: rgba(255, 255, 255, 0.46); color: #3f444c; font-size: 15px; font-weight: 700; line-height: 1; cursor: pointer; }
.nav-btn:disabled { opacity: .35; cursor: default; }
.nav-btn:not(:disabled):hover { background: rgba(255, 255, 255, 0.62); }
.dots { display: flex; flex-wrap: nowrap; align-items: center; justify-content: center; gap: 8px; min-width: 0; overflow-x: auto; padding: 0 2px; }
.dots button { flex-shrink: 0; min-width: 34px; height: 22px; padding: 0 10px; border-radius: 8px; border: 1px solid rgba(123, 129, 138, 0.86); background: rgba(255, 255, 255, 0.46); color: #3f444c; font-size: 12px; font-weight: 700; letter-spacing: -0.01em; line-height: 1; cursor: pointer; opacity: .72; }
.dots button.is-on { opacity: 1; background: rgba(73, 78, 86, 0.95); border-color: #fff; color: #fff; }
.blk { position: absolute; overflow: hidden; }
.text { white-space: pre-wrap; word-break: break-word; }
.img { overflow: hidden; background: #ececec; }
.img img { position: absolute; max-width: none; max-height: none; object-fit: fill; }
.ph, .title-empty { display: flex; align-items: center; justify-content: center; height: 100%; padding: 12px; color: #7a7f86; text-align: center; }
.shape { display: flex; align-items: center; justify-content: center; white-space: pre-wrap; word-break: break-word; padding: 4px; }
.shape.is-circle { box-sizing: border-box; }
.line { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; pointer-events: none; }
.table { padding: 0; }
.table table { width: 100%; height: 100%; border-collapse: collapse; table-layout: fixed; }
.table td { padding: 4px 6px; word-break: break-word; }
.btn { display: flex; align-items: center; justify-content: center; text-decoration: none; }
.link-card { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; padding: 10px; background: #111827; color: #fff; text-align: center; border-radius: 10px; }
.link-card a { color: #e9fe4e; font-weight: 700; word-break: break-all; }
.link-card .hint { font-size: 11px; opacity: .7; }
.quiz-short, .quiz-choice { overflow: visible; border: 3px solid rgba(150,154,160,.72); box-shadow: 0 10px 24px rgba(0,0,0,.16); }
.quiz-short { display: flex; align-items: center; justify-content: center; }
.quiz-short-input { width: 100%; height: 100%; border: 0; background: transparent; color: inherit; font: inherit; font-weight: inherit; text-align: center; outline: none; padding: 0 16px; box-sizing: border-box; }
.quiz-short-input::placeholder { color: rgba(26,26,26,.45); }
.quiz-short.is-wrong { border-color: #e53935; background: rgba(255,235,235,.98); }
.quiz-short.is-ok { border-color: #10504a; }
.quiz-short.is-ok .quiz-short-input { font-weight: 700; }
.quiz-choice { display: flex; align-items: center; justify-content: center; cursor: pointer; color: inherit; font: inherit; padding: 0; }
.quiz-choice.is-picked { border-color: #10504a; box-shadow: 0 0 0 2px rgba(26,122,110,.28), 0 10px 24px rgba(0,0,0,.16); }
.quiz-choice.is-wrong { border-color: #e53935; }
.quiz-choice.is-ok { border-color: #10504a; }
.overlay { position: absolute; inset: 0; display: none; align-items: center; justify-content: center; background: rgba(0,0,0,.52); z-index: 80; pointer-events: none; }
.overlay.is-on { display: flex; }
.overlay-mark { font-size: clamp(72px, 18vw, 180px); font-weight: 900; color: #fff; text-shadow: 0 8px 32px rgba(0,0,0,.35); }
.title-slide { display: none; flex-direction: column; padding: 28px 36px; }
.title-slide.is-on { display: flex; }
.title-tabs { display: flex; justify-content: center; flex-wrap: wrap; gap: 8px; margin-bottom: 18px; }
.title-tab { border: 0; background: #e5e7eb; color: #111; border-radius: 999px; padding: 6px 12px; cursor: pointer; }
.title-tab.is-on { background: #111; color: #fff; }
.title-panel { display: none; flex: 1; min-height: 0; overflow: auto; }
.title-panel.is-on { display: block; }
.title-hero { min-height: 100%; display: flex; align-items: center; justify-content: center; text-align: center; font-weight: 800; font-size: clamp(28px, 5vw, 60px); line-height: 1.3; white-space: pre-wrap; }
.title-lines { font-size: clamp(16px, 2.2vw, 28px); line-height: 1.55; }
.note { position: absolute; left: 16px; bottom: 18px; font-size: 11px; color: #9ca3af; }
</style>
</head>
<body>
  <header class="bar">
    <h1>에듀코디 클래스플래너 수업 프레젠테이션</h1>
  </header>
  ${renderOfflineHero(params.title)}
  <main class="stage">
    <div class="frame" id="frame">
      ${params.slidesHtml}
      <div class="overlay" id="quizOverlay" aria-hidden="true"><div class="overlay-mark" id="quizOverlayMark"></div></div>
    </div>
  </main>
  <nav class="nav">
    <button type="button" class="nav-btn" id="prev" aria-label="이전 슬라이드">&lt;</button>
    <div class="dots" id="dots"></div>
    <button type="button" class="nav-btn" id="next" aria-label="다음 슬라이드">&gt;</button>
  </nav>
<script>
(function () {
  var slides = Array.prototype.slice.call(document.querySelectorAll('.slide'));
  var dots = document.getElementById('dots');
  var prev = document.getElementById('prev');
  var next = document.getElementById('next');
  var index = 0;
  function show(nextIndex) {
    if (!slides.length) return;
    index = Math.max(0, Math.min(slides.length - 1, nextIndex));
    slides.forEach(function (el, i) { el.classList.toggle('is-on', i === index); });
    Array.prototype.forEach.call(dots.children, function (el, i) { el.classList.toggle('is-on', i === index); });
    prev.disabled = index <= 0;
    next.disabled = index >= slides.length - 1;
  }
  slides.forEach(function (_, i) {
    var label = slides[i].getAttribute('data-nav-label') || String(i + 1);
    var b = document.createElement('button');
    b.type = 'button';
    b.textContent = label;
    b.setAttribute('aria-label', label === '타이틀' ? '타이틀 슬라이드' : label + '번째 슬라이드');
    b.addEventListener('click', function () { show(i); });
    dots.appendChild(b);
  });
  prev.addEventListener('click', function () { show(index - 1); });
  next.addEventListener('click', function () { show(index + 1); });
  var overlay = document.getElementById('quizOverlay');
  var overlayMark = document.getElementById('quizOverlayMark');
  var overlayTimer = 0;
  function flashOverlay(mark) {
    if (!overlay || !overlayMark) return;
    overlayMark.textContent = mark;
    overlay.classList.add('is-on');
    if (overlayTimer) window.clearTimeout(overlayTimer);
    overlayTimer = window.setTimeout(function () { overlay.classList.remove('is-on'); }, 1000);
  }
  function normAnswer(s) { return String(s || '').trim().replace(/\\s+/g, ' '); }
  function parseAnswers(raw) {
    var t = String(raw || '');
    if (t.indexOf('#') === -1) {
      var one = normAnswer(t);
      return one ? [one] : [];
    }
    return t.split('#').map(normAnswer).filter(Boolean);
  }
  function isShortCorrect(input, raw) {
    var n = normAnswer(input);
    if (!n) return false;
    return parseAnswers(raw).indexOf(n) !== -1;
  }
  function parseCorrectIdx(raw) {
    return String(raw || '').split(',').map(function (v) { return parseInt(v, 10); }).filter(function (v) { return v >= 0 && v < 8; }).sort(function (a, b) { return a - b; });
  }
  function sameIdx(a, b) {
    if (!b.length || a.length !== b.length) return false;
    for (var i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false;
    return true;
  }
  function currentSlide() { return slides[index] || null; }
  function submitShort(box) {
    if (box.classList.contains('is-ok')) return;
    var input = box.querySelector('.quiz-short-input');
    if (!input) return;
    if (isShortCorrect(input.value, box.getAttribute('data-answer') || '')) {
      box.classList.remove('is-wrong');
      box.classList.add('is-ok');
      input.value = normAnswer(input.value);
      input.readOnly = true;
      flashOverlay('O');
    } else {
      box.classList.add('is-wrong');
      flashOverlay('X');
      window.setTimeout(function () { box.classList.remove('is-wrong'); }, 1000);
    }
  }
  function submitChoices(slide) {
    if (!slide) return;
    var groups = {};
    slide.querySelectorAll('.quiz-choice').forEach(function (btn) {
      var setId = btn.getAttribute('data-set-id') || '';
      if (!groups[setId]) groups[setId] = [];
      groups[setId].push(btn);
    });
    Object.keys(groups).forEach(function (setId) {
      var btns = groups[setId];
      if (btns.some(function (b) { return b.classList.contains('is-ok'); })) return;
      var picked = btns.filter(function (b) { return b.classList.contains('is-picked'); }).map(function (b) { return parseInt(b.getAttribute('data-index') || '-1', 10); }).filter(function (v) { return v >= 0; }).sort(function (a, b) { return a - b; });
      if (!picked.length) return;
      var correct = parseCorrectIdx(btns[0].getAttribute('data-correct') || '');
      if (sameIdx(picked, correct)) {
        btns.forEach(function (b) { b.classList.remove('is-wrong'); if (b.classList.contains('is-picked')) b.classList.add('is-ok'); });
        flashOverlay('O');
      } else {
        btns.forEach(function (b) { if (b.classList.contains('is-picked')) b.classList.add('is-wrong'); });
        flashOverlay('X');
        window.setTimeout(function () { btns.forEach(function (b) { b.classList.remove('is-wrong'); }); }, 1000);
      }
    });
  }
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.quiz-choice');
    if (!btn || btn.classList.contains('is-ok')) return;
    if (!currentSlide() || !currentSlide().contains(btn)) return;
    btn.classList.toggle('is-picked');
  });
  document.addEventListener('keydown', function (e) {
    var inQuiz = e.target && e.target.classList && e.target.classList.contains('quiz-short-input');
    if (inQuiz) {
      if (e.key === 'Enter') {
        e.preventDefault();
        var box = e.target.closest('.quiz-short');
        if (box) submitShort(box);
      }
      return;
    }
    if (e.key === 'Enter') { e.preventDefault(); submitChoices(currentSlide()); return; }
    if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') { e.preventDefault(); show(index + 1); }
    if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); show(index - 1); }
    if (e.key === 'Home') { e.preventDefault(); show(0); }
    if (e.key === 'End') { e.preventDefault(); show(slides.length - 1); }
  });
  document.querySelectorAll('.title-slide').forEach(function (slide) {
    slide.addEventListener('click', function (e) {
      var tab = e.target.closest('.title-tab');
      if (!tab) return;
      var id = tab.getAttribute('data-tab');
      slide.querySelectorAll('.title-tab').forEach(function (el) { el.classList.toggle('is-on', el === tab); });
      slide.querySelectorAll('.title-panel').forEach(function (el) { el.classList.toggle('is-on', el.getAttribute('data-panel') === id); });
    });
  });
  show(0);
})();
</script>
</body>
</html>`;
}

export async function downloadProgramSlideOfflineHtml(
  params: ProgramSlideOfflineHtmlExportParams
): Promise<void> {
  const extraPageCount = Math.max(0, params.extraPageCount);
  const order =
    params.editableSlideOrder.length > 0
      ? params.editableSlideOrder.filter((index) => index >= 0 && index < extraPageCount)
      : Array.from({ length: extraPageCount }, (_, index) => index);

  const canvasAspect = resolveOfflineSlideCanvasAspect(params.layouts, params.canvasAspect);
  const imageCache = new Map<string, string>();
  const canvasHtml: string[] = [];
  for (const pageIndex of order) {
    const raw = params.layouts[pageIndex] ?? EMPTY_SLIDE_LAYOUT;
    const inlined = await inlineLayoutImages(raw, imageCache);
    canvasHtml.push(renderCanvasSlide(inlined, canvasAspect, String(canvasHtml.length + 1)));
  }

  const canvasBg = resolveLayoutPageBackground(
    order.length > 0 ? params.layouts[order[0]] : undefined
  );
  const slidesHtml = [
    params.includeTitleSlide ? renderTitleSlide(params.title, canvasBg) : '',
    ...canvasHtml,
  ].join('');

  const heading =
    params.title.program.trim() ||
    params.title.learningTitle.trim() ||
    params.title.unit.trim() ||
    '수업 프레젠테이션';
  const html = buildOfflineHtmlDocument({
    heading,
    title: params.title,
    slidesHtml,
    canvasAspect,
    canvasBg,
  });

  const fileName = defaultOfflineHtmlFilename(params.fileNameHint?.trim() || heading);
  downloadTextFile(html, fileName);
}

